import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Stack,
} from "@mui/material";
import Chart from "react-apexcharts";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DashboardTabFilter from "../reuseable-components/DashboardTabFilter";
import {
  getSurveys,
  getSurveyStats,
} from "../../../apis/admin-apis/dashboard-apis/SurveyApis";

const RANK_ITEMS = [
  { key: "door", label: "Door knock" },
  { key: "phone", label: "Phone / Text" },
  { key: "literature", label: "Brochure" },
  { key: "digital", label: "Digital / Social" },
  { key: "mail", label: "Direct mail" },
  { key: "events", label: "Meet & greet" },
];

const AGE_GROUPS = ["18-25", "26-35", "36-45", "46-55", "55+"];

const AGE_FILTER_OPTIONS = [
  { label: "All Ages", value: "All" },
  ...AGE_GROUPS.map((g) => ({ label: g, value: g })),
];

const EMPTY_FILTERS = {
  search: "",
  zip_code: "",
  source: "All",
  from_date: "",
  to_date: "",
};

const PAGE_SIZE = 25;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const formatLocation = (ipLocation) => {
  if (!ipLocation) return "Unknown";
  const parts = [ipLocation.city, ipLocation.region, ipLocation.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Unknown";
};

const SurveysTab = () => {
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [viewSurvey, setViewSurvey] = useState(null);

  const load = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: PAGE_SIZE,
      };
      if (appliedFilters.search.trim()) params.search = appliedFilters.search.trim();
      if (appliedFilters.source && appliedFilters.source !== "All") {
        params.ageGroup = appliedFilters.source;
      }
      if (appliedFilters.from_date) params.fromDate = appliedFilters.from_date;
      if (appliedFilters.to_date) params.toDate = appliedFilters.to_date;

      const [listRes, statsRes] = await Promise.all([
        getSurveys(params),
        getSurveyStats(),
      ]);
      if (listRes?.success) {
        setSurveys(listRes.surveys || []);
        setTotalCount(listRes.total || 0);
      }
      if (statsRes?.success) setStats(statsRes.stats);
    } catch (err) {
      console.error("Failed to load surveys:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedFilters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const avgRankChart = useMemo(() => {
    if (!stats?.averageRanks) return { categories: [], values: [] };
    return {
      categories: RANK_ITEMS.map((i) => i.label),
      values: RANK_ITEMS.map((i) => stats.averageRanks[i.key] ?? 0),
    };
  }, [stats]);

  const ageChart = useMemo(() => {
    if (!stats?.ageBreakdown) return { labels: [], series: [] };
    const map = stats.ageBreakdown.reduce(
      (acc, row) => ({ ...acc, [row.ageGroup]: row.count }),
      {}
    );
    return {
      labels: AGE_GROUPS,
      series: AGE_GROUPS.map((g) => map[g] || 0),
    };
  }, [stats]);

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, bgcolor: "#fff" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F1E3D" }}>
          Voter Outreach Surveys
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
          How District 8 residents prefer to hear from the campaign.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: "1px solid #EAE2D1", mb: 3 }} />

      {/* Stats section — vertical stack so charts stay contained per row */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
            <StatCard
              label="TOTAL RESPONSES"
              value={isLoading ? null : stats?.total ?? 0}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }} sx={{ display: "flex" }}>
            <Card
              sx={{
                p: 2,
                borderRadius: "8px",
                boxShadow: "none",
                border: "1px solid #EAEAEA",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{ fontWeight: 700, color: "#0F1E3D", mb: 1 }}
                variant="subtitle2"
              >
                Preferred Outreach Channels (avg rank — lower is better)
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={240} />
              ) : (
                <Box sx={{ width: "100%" }}>
                  <Chart
                    type="bar"
                    height={240}
                    series={[{ name: "Avg Rank", data: avgRankChart.values }]}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        fontFamily: "inherit",
                        parentHeightOffset: 0,
                      },
                      colors: ["#0F1E3D"],
                      plotOptions: {
                        bar: { borderRadius: 4, columnWidth: "45%" },
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: (v) => (v ? Number(v).toFixed(1) : "-"),
                        style: { colors: ["#fff"] },
                      },
                      legend: { show: false },
                      xaxis: {
                        categories: avgRankChart.categories,
                        labels: { style: { fontSize: "11px" } },
                      },
                      yaxis: {
                        min: 0,
                        max: 6,
                        tickAmount: 6,
                        labels: { style: { fontSize: "11px" } },
                      },
                      tooltip: {
                        y: {
                          formatter: (v) =>
                            v ? `Avg rank: ${Number(v).toFixed(2)}` : "No data",
                        },
                      },
                      grid: { borderColor: "#f1f1f1" },
                    }}
                  />
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        <Card
          sx={{
            p: 2,
            borderRadius: "8px",
            boxShadow: "none",
            border: "1px solid #EAEAEA",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{ fontWeight: 700, color: "#0F1E3D", mb: 1 }}
            variant="subtitle2"
          >
            Age Group Distribution
          </Typography>
          {isLoading ? (
            <Skeleton variant="rectangular" height={220} />
          ) : (
            <Box sx={{ width: "100%" }}>
              <Chart
                type="bar"
                height={220}
                series={[{ name: "Responses", data: ageChart.series }]}
                options={{
                  chart: {
                    toolbar: { show: false },
                    fontFamily: "inherit",
                    parentHeightOffset: 0,
                  },
                  colors: ["#C4912E"],
                  plotOptions: {
                    bar: { borderRadius: 4, columnWidth: "40%" },
                  },
                  dataLabels: { enabled: false },
                  legend: { show: false },
                  xaxis: { categories: ageChart.labels },
                  yaxis: {
                    labels: {
                      formatter: (v) => Math.round(v),
                    },
                  },
                  grid: { borderColor: "#f1f1f1" },
                }}
              />
            </Box>
          )}
        </Card>
      </Stack>

      {/* Filters */}
      <DashboardTabFilter
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        showZipFilter={false}
        sourceLabel="Age Group"
        sourceOptions={AGE_FILTER_OPTIONS}
        searchPlaceholder="Search name, email, community..."
        singleLine
      />

      {/* Table */}
      <Card
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          border: "1px solid #EAEAEA",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #EAEAEA",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#0F1E3D" }}>
            Submissions ({totalCount})
          </Typography>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8F5EE" }}>
                {[
                  "Submitted",
                  "Name",
                  "Email",
                  "Community",
                  "Age",
                  "Location",
                  "IP Address",
                  "Rankings",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, color: "#0F1E3D", fontSize: 13 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : surveys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="#666">
                      No survey responses yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                surveys.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ fontSize: 13 }}>
                      {formatDate(row.createdAt)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {row.name || <em style={{ color: "#999" }}>anonymous</em>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {row.email || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {row.community || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      <Chip
                        label={row.ageGroup}
                        size="small"
                        sx={{
                          bgcolor: "#EAF0F7",
                          color: "#0B3D6E",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 14, color: "#C4912E" }} />
                        {formatLocation(row.ipLocation)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontFamily: "monospace" }}>
                      {row.ipAddress || "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setViewSurvey(row)}
                        sx={{
                          textTransform: "none",
                          fontSize: 12,
                          height: 28,
                          bgcolor: "#0B3D6E",
                          color: "#fff",
                          fontWeight: 600,
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: "#072844",
                            boxShadow: "none",
                          },
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count}`
          }
        />
      </Card>

      {/* Rankings viewer */}
      <Dialog
        open={!!viewSurvey}
        onClose={() => setViewSurvey(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            bgcolor: "#0F1E3D",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Survey Rankings</Typography>
          <IconButton
            onClick={() => setViewSurvey(null)}
            sx={{ color: "#fff" }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          {viewSurvey && (
            <>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="caption" color="#666">
                  Submitted {formatDate(viewSurvey.createdAt)} · Age{" "}
                  {viewSurvey.ageGroup}
                </Typography>
                {viewSurvey.name && (
                  <Typography variant="body2">
                    <b>Name:</b> {viewSurvey.name}
                  </Typography>
                )}
                {viewSurvey.email && (
                  <Typography variant="body2">
                    <b>Email:</b> {viewSurvey.email}
                  </Typography>
                )}
                {viewSurvey.community && (
                  <Typography variant="body2">
                    <b>Community:</b> {viewSurvey.community}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: "#C4912E" }} />
                  <b>Location:</b>&nbsp;{formatLocation(viewSurvey.ipLocation)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  <b style={{ fontFamily: "inherit" }}>IP:</b> {viewSurvey.ipAddress || "-"}
                </Typography>
              </Stack>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 1, color: "#0F1E3D" }}
              >
                Preferred outreach (1 = most preferred)
              </Typography>
              <Stack spacing={1}>
                {RANK_ITEMS.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.75,
                      px: 1.25,
                      bgcolor: "#F8F5EE",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">{item.label}</Typography>
                    <Chip
                      label={viewSurvey.rankings?.[item.key] ?? "-"}
                      size="small"
                      sx={{
                        bgcolor: "#0B3D6E",
                        color: "#fff",
                        fontWeight: 700,
                        minWidth: 32,
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
};

const StatCard = ({ label, value }) => (
  <Card
    sx={{
      p: 2,
      borderRadius: "8px",
      boxShadow: "none",
      border: "1px solid #EAEAEA",
      borderLeftWidth: 5,
      borderLeftStyle: "solid",
      borderLeftColor: "#0B3D6E",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Typography variant="h3" sx={{ fontWeight: 700, color: "#0B3D6E" }}>
      {value === null ? <Skeleton width="50%" /> : value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color: "#999",
        letterSpacing: "0.5px",
        mt: 0.5,
      }}
    >
      {label}
    </Typography>
  </Card>
);

export default SurveysTab;
